import type { Http } from '../http';
import { endpoints } from '../endpoints';
import type { RawCommonLabel, RequestOptions } from '../types/common';
import type {
  RawCollectPlantInfo,
  RawEndUserPlantInfoResponse,
  RawEnergyTypeField,
  RawHistoryFilterInfo,
  RawHistoryFilterInfoRequest,
  RawPicPath,
  RawPlantDetailRangedRequest,
  RawPlantDetailRangedResponse,
  RawPlantHistoryRequest,
  RawPlantHistoryResponse,
  RawPlantListCountResponse,
  RawPlantListRequest,
  RawPlantListResponse,
  RawPlantOperationDevicesRequest,
  RawPlantOperationDevicesResponse,
  RawPlantOperationInfo,
  RawPlantOperationRequest,
  RawPlantOperationStatusRequest,
  RawPlantOperationStatusResponse,
} from '../types/plant';

/**
 * PlantController collect/* 호출 함수. 조회도 대부분 POST + body(레거시).
 * 반환값은 BaseResponse 언랩 후 payload (factory 인터셉터가 언랩 — create-heviton-api-web-legacy.ts).
 */
export function createPlantApi(http: Http) {
  return {
    /** POST /collect/plant/list — 발전소 목록 (offset/limit 페이지네이션) */
    async list(body: RawPlantListRequest, options?: RequestOptions): Promise<RawPlantListResponse> {
      const res = await http.post<RawPlantListResponse>(endpoints.plant.list, body, {
        signal: options?.signal,
      });
      return res.data;
    },

    /** POST /collect/plant/list/count — 목록 카운트 (total/filtered) */
    async listCount(
      body: RawPlantListRequest,
      options?: RequestOptions,
    ): Promise<RawPlantListCountResponse> {
      const res = await http.post<RawPlantListCountResponse>(endpoints.plant.listCount, body, {
        signal: options?.signal,
        timeout: options?.timeout,
      });
      return res.data;
    },

    /** POST /collect/plant/operation/info — 운영 현황 요약 */
    async operationInfo(
      body: RawPlantOperationRequest,
      options?: RequestOptions,
    ): Promise<RawPlantOperationInfo> {
      const res = await http.post<RawPlantOperationInfo>(endpoints.plant.operationInfo, body, {
        signal: options?.signal,
      });
      return res.data;
    },

    /** POST /collect/plant/operation/devices — 설비 구성 */
    async operationDevices(
      body: RawPlantOperationDevicesRequest,
      options?: RequestOptions,
    ): Promise<RawPlantOperationDevicesResponse> {
      const res = await http.post<RawPlantOperationDevicesResponse>(
        endpoints.plant.operationDevices,
        body,
        { signal: options?.signal },
      );
      return res.data;
    },

    /** POST /collect/plant/operation/status — 가동률 현황 */
    async operationStatus(
      body: RawPlantOperationStatusRequest,
      options?: RequestOptions,
    ): Promise<RawPlantOperationStatusResponse> {
      const res = await http.post<RawPlantOperationStatusResponse>(
        endpoints.plant.operationStatus,
        body,
        { signal: options?.signal },
      );
      return res.data;
    },

    /** POST /collect/plant/detail/ranged — 발전 추이 (기간 범위) */
    async detailRanged(
      body: RawPlantDetailRangedRequest,
      options?: RequestOptions,
    ): Promise<RawPlantDetailRangedResponse> {
      const res = await http.post<RawPlantDetailRangedResponse>(
        endpoints.plant.detailRanged,
        body,
        { signal: options?.signal },
      );
      return res.data;
    },

    /** POST /collect/plant/historyFilterInfo — 이력 필터 정보 (응답 배열) */
    async historyFilterInfo(
      body: RawHistoryFilterInfoRequest,
      options?: RequestOptions,
    ): Promise<RawHistoryFilterInfo[]> {
      const res = await http.post<RawHistoryFilterInfo[]>(endpoints.plant.historyFilterInfo, body, {
        signal: options?.signal,
      });
      return res.data;
    },

    /** POST /collect/plant/history — 발전/에러 이력 */
    async history(
      body: RawPlantHistoryRequest,
      options?: RequestOptions,
    ): Promise<RawPlantHistoryResponse> {
      const res = await http.post<RawPlantHistoryResponse>(endpoints.plant.history, body, {
        signal: options?.signal,
      });
      return res.data;
    },

    /** GET /collect/plant/info/{serialNo} — 발전소 상세 */
    async info(serialNo: string, options?: RequestOptions): Promise<RawCollectPlantInfo> {
      const res = await http.get<RawCollectPlantInfo>(endpoints.plant.info(serialNo), {
        signal: options?.signal,
      });
      return res.data;
    },

    /** GET /collect/end/plantInfo — 엔드유저 발전소 목록 */
    async endUserInfo(options?: RequestOptions): Promise<RawEndUserPlantInfoResponse> {
      const res = await http.get<RawEndUserPlantInfoResponse>(endpoints.plant.endUserInfo, {
        signal: options?.signal,
      });
      return res.data;
    },

    /** GET /collect/pic/path/{orderId} — 갤러리 사진 경로 (응답 배열) */
    async picPath(orderId: string, options?: RequestOptions): Promise<RawPicPath[]> {
      const res = await http.get<RawPicPath[]>(endpoints.plant.picPath(orderId), {
        signal: options?.signal,
      });
      return res.data;
    },

    /** GET /collect/energy/types/input-fields/{energyCode} — 입력 필드 정의 (응답 배열) */
    async energyTypeInputFields(
      energyCode: string,
      options?: RequestOptions,
    ): Promise<RawEnergyTypeField[]> {
      const res = await http.get<RawEnergyTypeField[]>(
        endpoints.plant.energyTypeInputFields(energyCode),
        { signal: options?.signal },
      );
      return res.data;
    },

    /** GET /collect/custom/energyCodeList — 자격 에너지 코드 목록 (응답 배열) */
    async customEnergyCodeList(options?: RequestOptions): Promise<RawCommonLabel[]> {
      const res = await http.get<RawCommonLabel[]>(endpoints.plant.customEnergyCodeList, {
        signal: options?.signal,
      });
      return res.data;
    },
  };
}
